import isBefore from "date-fns/isBefore";
import _ from "lodash";
import { createContext, useContext, useState } from "react";
import { toast } from "react-toastify";

import ProjectService from "../services/project.service";
import ProjectSummaryService from "../services/projectSummary.service";
import {
  Project,
  ProjectAssignTime,
  ProjectClient,
  ProjectEvent,
  ProjectFinance,
  ProjectRequester,
  ProjectStatus,
  ProjectSummary,
  ProjectSummaryContext,
  TabActive,
} from "../types";
import { BudgetStatus, getBudgetCurrentStatus } from "../types/budget/budget";
import { BudgetListItem } from "../utils/budget-adapters";
import {
  TRANSITION_REQUIREMENTS,
  isSameStatusStay,
} from "../utils/briefingStatus";

export type ProjectContextProps = {
  notification?: any;
  loadingProject?: boolean;

  project?: Project;
  client?: ProjectClient;
  requester?: ProjectRequester;
  assignTime?: ProjectAssignTime;
  event?: ProjectEvent;
  finance?: ProjectFinance;
  summary?: any;
  unsavedSummary?: any;

  loadProject: (projectId: string) => void;
  closeNotification: () => void;
  showNotification: (message: string, severity: string) => void;

  updateStatus: (
    status: ProjectStatus,
    isReturn?: boolean,
    budgetList?: BudgetListItem[],
    closingStatus?: string | null
  ) => void;
  getStatusPendencies: (
    status: string,
    budgetList?: BudgetListItem[],
    closingStatus?: string | null
  ) => string[];
  updateClient: (client: ProjectClient) => void;
  updateAssignTime: (assignTime: ProjectAssignTime) => void;
  updateEvent: (event: ProjectEvent) => Promise<void>;
  updateFinance: (finance: ProjectFinance) => void;
  updateRequester: (requester: ProjectRequester) => void;
  updateSummaryContext: (
    type: "ON_APPROVAL" | "APPROVED" | "CLOSURE" | "AUDITED",
    summary: ProjectSummary
  ) => void;
  clearProject: () => void;
  tabActive: number;
  setTabActive: (step: number) => void;
  handleSetUnsavedSummary: (value: any) => void;
  // Campos Gerenciais que bloquearam a troca de status, destacados em Dados
  // do Evento após "Ir para Dados Cadastrais".
  pendingManagementFields: string[];
  goToPendingManagementFields: (fields: string[]) => void;
  clearPendingManagementFields: () => void;
  // Etapa de Dados Cadastrais aberta pelos links dos cards da aba Overview
  // (0 Cliente, 1 Atribuir time, 2 Dados do evento).
  registrationStep: RegistrationStep | null;
  goToRegistrationStep: (step: RegistrationStep) => void;
  clearRegistrationStep: () => void;
  confirmEventData: () => Promise<void>;
};

export enum RegistrationStep {
  CLIENT = 0,
  TEAM = 1,
  EVENT = 2,
}

const ProjectContext = createContext<ProjectContextProps>(
  {} as ProjectContextProps
);

function ProjectProviderNew({ children }: any) {
  const [loadingProject, setLoadingProject] = useState(false);

  const [notification, setNotification] = useState<any>({
    open: false,
    message: "",
    severity: "success",
  });

  const [project, setProject] = useState<any>();
  const [client, setClient] = useState<ProjectClient>();
  const [requester, setRequester] = useState<ProjectRequester>();
  const [assignTime, setAssignTime] = useState<ProjectAssignTime>();
  const [event, setEvent] = useState<ProjectEvent>();
  const [finance, setFinance] = useState<ProjectFinance>();
  const [summary, setSummary] = useState<any>();
  const [tabActive, setTabActive] = useState(0);
  const [pendingManagementFields, setPendingManagementFields] = useState<
    string[]
  >([]);
  const [registrationStep, setRegistrationStep] =
    useState<RegistrationStep | null>(null);
  const [unsavedSummary, setUnsavedSummary] = useState({
    active: false,
    saveFn: null,
  });

  function getActionLabel() {
    return project?._id ? "atualizado" : "adicionado";
  }

  async function updateClient(client: ProjectClient) {
    if (project?._id) {
      await ProjectService.update(project._id, {
        client: client.clientSelected._id,
        requestings: client.requestings.map((requesting) => requesting._id),
      });

      await reloadLastUpdated(project._id);
    }

    setClient(client);
    showNotification(`Cliente ${getActionLabel()} com sucesso`);
  }
  async function updateRequester(requester: ProjectRequester) {
    if (project?._id)
      await ProjectService.update(project._id, {
        requester: requester._id,
      });

    setRequester(requester);
  }

  async function updateAssignTime(assignTime: ProjectAssignTime) {
    if (project?._id) {
      await ProjectService.update(project._id, {
        commercial: assignTime.commercial._id,
        firstAttendance: assignTime.firstAttendance._id,
        secondAttendance: assignTime.secondAttendance?._id,

        groups: assignTime.groups
          .filter((projectGroup) => projectGroup.group._id)
          .map((projectGroup) => ({
            group: projectGroup.group?._id,
            leader: projectGroup.leader?._id,
            firstCoordinator: projectGroup.firstCoordinator?._id,
            secondCoordinator: projectGroup.secondCoordinator?._id,
          })),
      });

      await reloadLastUpdated(project._id);
    }

    setAssignTime(assignTime);
    showNotification(`Time ${getActionLabel()} com sucesso`);
  }

  function showNotification(message: string, severity = "success") {
    setNotification({ open: true, message, severity });
  }

  function closeNotification() {
    setNotification(
      (prev: { open: boolean; message: string; severity: string }) => ({
        ...prev,
        open: false,
      })
    );
  }

  async function updateStatus(
    status: ProjectStatus,
    isReturn?: boolean,
    budgetList: BudgetListItem[] = [],
    closingStatus?: string | null
  ) {
    const isSameStatus = isSameStatusStay(
      project?.status?.status,
      status.status
    );
    if (!isReturn && !isSameStatus) {
      const errors = validateCanHaveStatus(
        status.status,
        budgetList,
        closingStatus
      );
      if (errors?.error) {
        // Erro estruturado — o modal exibe todas as pendências em um único modal
        const validationError = new Error(
          "STATUS_VALIDATION_ERROR"
        ) as Error & { pendencies?: string[] };
        validationError.pendencies = errors.messages;
        throw validationError;
      }
    }
    if (project?._id) await ProjectService.updateStatus(status);

    setProject({ ...project, status });
  }

  async function updateEvent(event: ProjectEvent) {
    if (project?._id) {
      await ProjectService.update(project._id, { event });
      await reloadLastUpdated(project._id);
    } else {
      await createProject(event);
    }

    setEvent(event);
    showNotification(`Evento ${getActionLabel()} com sucesso`);
  }

  function updateSummaryContext(
    type: "ON_APPROVAL" | "APPROVED" | "CLOSURE" | "AUDITED",
    newSummary: any
  ) {
    setSummary({ ...summary, [type]: newSummary });
  }

  async function updateFinance(finance: ProjectFinance) {
    if (project?._id) {
      await ProjectService.update(project._id, { finance });
      await reloadLastUpdated(project._id);
    } else {
    }

    setFinance(finance);
    showNotification(`Previsão de custo ${getActionLabel()} com sucesso`);
  }

  function validateFinancialFields() {
    if (finance) {
      const errorObj = {
        error: false,
        messages: [] as string[],
      };
      for (const [key, value] of Object.entries(finance)) {
        switch (key) {
          case "billingDeadline":
            if (value.trim() === "") {
              errorObj.error = true;
              errorObj.messages.push(
                'No status "Em aprovação" o campo Prazo de faturamento é obrigatório'
              );
            }
            break;
          case "paymentMethod":
            if (value.trim() === "") {
              errorObj.error = true;
              errorObj.messages.push(
                'No status "Em aprovação" o campo Forma de pagamento é obrigatório'
              );
            }
            break;
          case "debitNote":
            if (value.trim() === "") {
              errorObj.error = true;
              errorObj.messages.push(
                'No status "Em aprovação" o campo Nota de débito é obrigatório'
              );
            }
            break;
        }
      }
      return errorObj;
    }
    return {
      error: true,
      messages: [
        'Nâo é possível atualizar o status para "Em aprovação" sem que os valores de previsão de custo estejam preenchidos',
      ],
    };
  }
  interface Obj {
    [key: string]: any;
  }
  const requiredFields: Obj = {
    local: "Local do Evento", //  removido no redesign de 03/25
    zip_code: "CEP",
    city: "Cidade",
    state: "Estado",
    country: "País",
    complement: "Logradouro",
    number: "Número",
  };

  const fieldTranslations: Obj = {
    totalValueOfEvents: "Valor total do evento",
    directPaymentOfClients: "Cliente possui adiantamento ?",
    logisticFee: "Fee de logística",
    logisticMarkup: "Markup de logística",
    logisticComission: "Comissão de logística",
    airFee: "Fee de aéreo",
    airMarkup: "Markup de aéreo",
    airComission: "Comissão de aéreo",
    RSVP: "RSVP",
    communicationFee: "Fee de comunicação",
    taxes: "Impostos",
    customerBudget: "Budget do cliente",
    acceptAdvances: "Aceita adiantamento ?",
    billingMethod: "Forma de faturamento",
    taxPercentage: "Percentual de imposto",
  };

  function getErrorMessage(key: string) {
    return `O campo ${requiredFields[key]} é obrigatório`;
  }

  function fieldErrorMessage(field: string) {
    return `O campo "${fieldTranslations[field]}" é obrigatório`;
  }

  function validateAddressFields() {
    if (event) {
      const errorObj = {
        error: false,
        messages: [] as string[],
      };
      for (const [key, value] of Object.entries(event)) {
        if (!value && !!requiredFields[key]) {
          errorObj.error = true;
          errorObj.messages.push(getErrorMessage(key));
        }
      }
      return errorObj;
    }
    return {
      error: true,
      messages: [
        "Nâo é possível atualizar o status sem que os valores de previsão de custo estejam preenchidos",
      ],
    };
  }

  function handleSetUnsavedSummary(value: any) {
    setUnsavedSummary(value);
  }

  function validateSummaryFields(status: string) {
    if (event) {
      const errorObj = {
        error: false,
        messages: [] as string[],
      };
      const summaryRequiredFields = [
        "totalValueOfEvents",
        "directPaymentOfClients",
        "logisticFee",
        "logisticMarkup",
        "logisticComission",
        "airFee",
        "airMarkup",
        "airComission",
        "RSVP",
        "communicationFee",
      ];

      const eventRequiredFields = [
        "customerBudget",
        "acceptAdvances",
        "billingMethod",
      ];

      for (const i in summaryRequiredFields) {
        const fieldName = summaryRequiredFields[i];
        if (
          fieldName === "directPaymentOfClients" &&
          (event as any)?.acceptAdvances === false
        ) {
          continue;
        }
        const currentField = summary[status]
          ? summary[status][fieldName]
          : undefined;
        if (currentField === undefined || currentField === null) {
          errorObj.error = true;
          errorObj.messages.push(fieldErrorMessage(fieldName));
        }
      }

      const eventCtx = event as any;

      for (const i in eventRequiredFields) {
        const currentField = eventCtx[eventRequiredFields[i]];
        if (
          currentField === undefined ||
          currentField === null ||
          currentField === ""
        ) {
          errorObj.error = true;
          errorObj.messages.push(fieldErrorMessage(eventRequiredFields[i]));
        }
      }

      return errorObj;
    }
    return {
      error: true,
      messages: [
        "Nâo é possível atualizar o status sem que os valores de dados do evento estejam preenchidos",
      ],
    };
  }

  // Mensagem exata definida no card
  const NO_CLOSING_BUDGET_MESSAGE =
    "Para aprovar o projeto, aprove o orçamento ou a negociação aceita pelo cliente clicando em Aprovar";

  // Valida existência de orçamento/negociação na etapa de Fechamento.
  // O status efetivo de um orçamento vem da última entrada de versionHistory;
  // ao aprovar (botão "Aprovar" -> Fechamento) esse status passa a ser "APPROVED".
  function validateClosingBudget(budgetList: BudgetListItem[] = []) {
    const errorObj = {
      error: false,
      messages: [] as string[],
    };

    const hasClosingBudget = budgetList.some(
      (budget) =>
        budget?.status !== "DELETED" &&
        getBudgetCurrentStatus(budget?.versionHistory) === "APPROVED"
    );

    if (!hasClosingBudget) {
      errorObj.error = true;
      errorObj.messages.push(NO_CLOSING_BUDGET_MESSAGE);
    }
    return errorObj;
  }

  // Mensagem exata definida no card
  const NO_PRE_CLOSING_MESSAGE =
    "Para alterar o status do projeto para Pré-Fechamento, acesse o orçamento aprovado na etapa de Fechamento e clique no botão Pré-Fechamento.\nCertifique-se de que os valores, extras e itens realizados no evento estejam atualizados antes de concluir essa ação.";

  // Valida se o botão Pré-Fechamento (closingControl) já foi acionado no orçamento
  // antes de permitir a transição Finalizado -> Pré-Fechamento
  function validatePreClosing(closingStatus?: string | null) {
    const errorObj = {
      error: false,
      messages: [] as string[],
    };

    if (closingStatus !== "PRE_CLOSING") {
      errorObj.error = true;
      errorObj.messages.push(NO_PRE_CLOSING_MESSAGE);
    }
    return errorObj;
  }

  function validateCanHaveStatus(
    status: string,
    budgetList: BudgetListItem[] = [],
    closingStatus?: string | null
  ) {
    const createdProjectDate = project?.createdAt;

    if (!createdProjectDate) return null;

    // const summaryValidationDate = new Date(2024, 6, 31, 23, 59, 0);

    // const shouldValidateSummary = !isBefore(
    //   new Date(createdProjectDate),
    //   summaryValidationDate
    // );

    // if (!shouldValidateSummary) return null;

    const requirements = TRANSITION_REQUIREMENTS[status];
    const summaryException = TRANSITION_REQUIREMENTS[status]?.summaryException;
    const isExceptionSummary = Array.isArray(summaryException)
      ? summaryException.includes(project?.status?.status)
      : summaryException === project?.status?.status;

    let addressFields = null;
    let summaryFields = null;
    let closingBudgetFields = null;
    let preClosingFields = null;

    if (requirements) {
      if (requirements.address) {
        addressFields = validateAddressFields();
      }

      const activeBudgets = budgetList.filter((b) => b?.status !== "DELETED");
      const hasBudget = activeBudgets.length > 0;

      if (requirements.summary && !isExceptionSummary) {
        summaryFields = validateSummaryFields(requirements.summary);
      }

      // Exceção: projeto ainda não usa o sistema de orçamento.
      // Sem orçamento, não há Fechamento a validar — dados cadastrais bastam.
      if (requirements.closingBudget && hasBudget) {
        closingBudgetFields = validateClosingBudget(budgetList);
      }

      // Exceção: projeto ainda não usa o sistema de orçamento.
      // Sem orçamento, não há botão Pré-Fechamento a acionar — permite a transição.
      if (requirements.requiresPreClosing && hasBudget) {
        preClosingFields = validatePreClosing(closingStatus);
      }

      if (
        addressFields?.error ||
        summaryFields?.error ||
        closingBudgetFields?.error ||
        preClosingFields?.error
      ) {
        const mergedMessages = [
          ...(addressFields?.messages || []),
          ...(summaryFields?.messages || []),
          ...(closingBudgetFields?.messages || []),
          ...(preClosingFields?.messages || []),
        ];
        return {
          error: true,
          messages: mergedMessages,
        };
      }
      return null;
    }
    return { error: true, messages: [] };
  }

  // Usado pelo modal para montar o único modal de pendências (não dispara toast)
  function getStatusPendencies(
    status: string,
    budgetList: BudgetListItem[] = [],
    closingStatus?: string | null
  ): string[] {
    const result = validateCanHaveStatus(status, budgetList, closingStatus);
    return result?.error ? result.messages : [];
  }

  async function reloadLastUpdated(projectId: string) {
    const projectData = await ProjectService.get(projectId).then();
    setProject({
      ...project,
      updatedAt: projectData.updatedAt,
      lastUserUpdated:
        projectData?.lastUserUpdated ?? projectData?.projectLastUserUpdated,
    });
  }

  async function loadProject(projectId: string) {
    setLoadingProject(true);
    const projectData = await ProjectService.get(projectId).then();

    const [onApprovalSummary, approvedSummary, closureSummary, auditedSumamry] =
      await Promise.all([
        ProjectSummaryService.get({
          project: projectId,
          status: BudgetStatus.ON_APPROVAL,
        }),
        ProjectSummaryService.get({
          project: projectId,
          status: BudgetStatus.APPROVED,
        }),
        ProjectSummaryService.get({
          project: projectId,
          status: BudgetStatus.CLOSURE,
        }),
        ProjectSummaryService.get({
          project: projectId,
          status: BudgetStatus.AUDITED,
        }),
      ]);

    setProject({
      ...projectData,
      lastUserUpdated:
        projectData.lastUserUpdated ?? projectData.projectLastUserUpdated,
    });

    const {
      requestings,
      event,
      client,
      commercial,
      firstAttendance,
      secondAttendance,
      groups,
      ...finance
    } = projectData;

    setClient({ clientSelected: client, requestings });

    setEvent({
      ...event,
      startDate: new Date(event.startDate),
      endDate: new Date(event.endDate),
      sendDate: new Date(event.sendDate),
      briefingStartDate: new Date(event.briefingStartDate),
      presentationDate:
        event.presentationDate && new Date(event.presentationDate),
    });
    setAssignTime({
      commercial: commercial ?? {},
      firstAttendance: firstAttendance ?? {},
      secondAttendance: secondAttendance ?? {},
      groups,
    });
    setFinance(finance);
    setSummary({
      ON_APPROVAL: onApprovalSummary?.[0],
      APPROVED: approvedSummary?.[0],
      CLOSURE: closureSummary?.[0],
      AUDITED: auditedSumamry?.[0],
    });

    setLoadingProject(false);
  }

  async function createProject(event: ProjectEvent) {
    if (!client) return showNotification("Selecione um cliente", "error");
    if (!assignTime) return showNotification("Selecione um time", "error");
    if (!event) return showNotification("Preencha o evento", "error");

    const data = {
      client: client.clientSelected._id,
      requestings: client.requestings.map((requesting) => requesting._id),
      commercial: assignTime.commercial._id,
      firstAttendance: assignTime.firstAttendance._id,
      secondAttendance: assignTime.secondAttendance?._id,

      groups: assignTime.groups
        .filter((projectGroup) => projectGroup.group._id)
        .map((projectGroup) => ({
          group: projectGroup.group?._id,
          leader: projectGroup.leader?._id,
          firstCoordinator: projectGroup.firstCoordinator?._id,
          secondCoordinator: projectGroup.secondCoordinator?._id,
        })),

      event,
      requester,
    };

    const project = await ProjectService.create(data);

    await Promise.all([
      ProjectSummaryService.create({
        project: project?._id,
        status: BudgetStatus.ON_APPROVAL,
      }),
      ProjectSummaryService.create({
        project: project?._id,
        status: BudgetStatus.APPROVED,
      }),
      ProjectSummaryService.create({
        project: project?._id,
        status: BudgetStatus.CLOSURE,
      }),
      ProjectSummaryService.create({
        project: project?._id,
        status: BudgetStatus.AUDITED,
      }),
    ]);

    setProject(project);
  }

  function goToPendingManagementFields(fields: string[]) {
    setPendingManagementFields(fields);
    setTabActive(TabActive.INITIAL);
  }

  function clearPendingManagementFields() {
    setPendingManagementFields([]);
  }

  function goToRegistrationStep(step: RegistrationStep) {
    setRegistrationStep(step);
    setTabActive(TabActive.INITIAL);
  }

  function clearRegistrationStep() {
    setRegistrationStep(null);
  }

  async function confirmEventData() {
    if (!project?._id) return;
    const confirmation = await ProjectService.confirmEventData(project._id);
    setProject((prev: Project | undefined) =>
      prev ? { ...prev, ...confirmation } : prev
    );
  }

  function clearProject() {
    setProject(undefined);
    setClient(undefined);
    setAssignTime(undefined);
    setEvent(undefined);
    setFinance(undefined);
    setTabActive(0);
    setPendingManagementFields([]);
    setRegistrationStep(null);
  }

  return (
    <ProjectContext.Provider
      value={{
        tabActive,
        notification,
        loadingProject,

        client,
        project,
        assignTime,
        event,
        finance,
        summary,
        unsavedSummary,

        setTabActive,
        loadProject,
        showNotification,
        closeNotification,
        updateStatus,
        getStatusPendencies,
        updateClient,
        updateAssignTime,
        updateEvent,
        updateFinance,
        updateRequester,
        updateSummaryContext,
        clearProject,
        handleSetUnsavedSummary,
        pendingManagementFields,
        goToPendingManagementFields,
        clearPendingManagementFields,
        registrationStep,
        goToRegistrationStep,
        clearRegistrationStep,
        confirmEventData,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
}

const useProject = () => useContext(ProjectContext);
export { ProjectProviderNew, useProject };
